// JobLogReviewPage.jsx
import React, { useEffect, useState, useContext } from "react";
import secureAxios from "../../utils/secureAxios";
import { formatDate } from "../../utils/dateFormatter";
import { AuthContext } from "../../contexts/AuthContext";
import { toast } from "react-toastify";

// Reusable HIPAA Compliance Components
const PHIWarningBanner = () => (
  <div className="alert alert-warning mb-3">
    <h6 className="alert-heading">
      <i className="bi bi-shield-lock-fill"></i> Protected Health Information
      (PHI)
    </h6>
    <small>
      This page contains Protected Health Information. Access is logged and
      monitored for HIPAA compliance. Only access information necessary for
      payment processing. Unauthorized access or disclosure may result in civil
      and criminal penalties under HIPAA.
    </small>
  </div>
);

const PurposeOfUseBanner = ({ purpose = "Payment Processing" }) => (
  <div className="alert alert-info py-2 mb-3">
    <div className="d-flex justify-content-between align-items-center flex-wrap">
      <span>
        <strong>Purpose of Use:</strong> {purpose}
      </span>
      <span>
        <strong>Access Time:</strong> {new Date().toLocaleString()}
      </span>
    </div>
  </div>
);

const DataHandlingNotice = () => (
  <div className="mt-4 p-3 bg-light rounded">
    <h6 className="text-muted">Data Handling Notice:</h6>
    <ul className="small text-muted mb-0">
      <li>All actions on this page are logged for audit purposes</li>
      <li>
        Do not share, screenshot, or export this information outside approved
        channels
      </li>
      <li>
        Report any suspected privacy breaches immediately to your Privacy
        Officer
      </li>
      <li>Access is limited to the minimum necessary for job duties</li>
    </ul>
  </div>
);

const JobLogReviewPage = () => {
  const [jobLogs, setJobLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    is_paid: "",
    is_billable: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLogs, setSelectedLogs] = useState([]);

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [pendingPayment, setPendingPayment] = useState(null); // { type: 'single' | 'bulk', logId?: string }

  const { user, loadingUser } = useContext(AuthContext);
  const is_admin = user?.is_admin;
  const tenant_id = user?.tenant_id;

  useEffect(() => {
    if (loadingUser || !is_admin || !tenant_id) return;
    fetchJobLogs();
  }, [is_admin, tenant_id, loadingUser, filters.is_paid, filters.is_billable]);

  const fetchJobLogs = async () => {
    try {
      setLoading(true);
      setSelectedLogs([]);

      const queryParams = {
        // Remove date filtering from API - load all data
        is_paid: filters.is_paid === "" ? undefined : filters.is_paid,
        is_billable:
          filters.is_billable === "" ? undefined : filters.is_billable,
      };

      Object.keys(queryParams).forEach((key) => {
        if (!queryParams[key]) delete queryParams[key];
      });

      const res = await secureAxios.get("/api/job-logs/review", {
        params: queryParams,
      });

      setJobLogs(res.data);
    } catch (err) {
      console.error("Error loading job logs");
      toast.error("Failed to load job logs");
    } finally {
      setLoading(false);
    }
  };

  const markAsPaid = (log_id) => {
    // Open the payment modal instead of confirming directly
    const log = jobLogs.find((l) => l.job_log_id === log_id);
    setPendingPayment({
      type: "single",
      logId: log_id,
      amount: Number(log?.amount_to_pay || 0),
    });
    setPaymentDate(new Date().toISOString().split("T")[0]);
    setShowPaymentModal(true);
  };

  const markAsUnpaid = async (log_id) => {
    try {
      const log = jobLogs.find((l) => l.job_log_id === log_id);
      const amountPaid = Number(log?.amount_paid || log?.amount_to_pay || 0);

      if (
        !window.confirm(
          `Mark this job log as UNPAID?\n\n` +
            `Amount: $${amountPaid.toFixed(2)}\n` +
            `Date Paid: ${formatDate(log?.paid_at)}\n\n` +
            `By confirming, you acknowledge that:\n` +
            `- This action will be logged for audit purposes\n` +
            `- You are reversing a previous payment record\n` +
            `- You are authorized to process this reversal`
        )
      ) {
        return;
      }

      await secureAxios.patch(`/api/job-logs/${log_id}/mark-unpaid`);

      toast.success("Job log marked as unpaid");
      fetchJobLogs();
    } catch (err) {
      console.error("Error marking as unpaid");
      toast.error("Failed to mark job log as unpaid");
    }
  };

  const confirmPayment = async () => {
    if (!pendingPayment) return;

    try {
      if (pendingPayment.type === "single") {
        await secureAxios.patch(
          `/api/job-logs/${pendingPayment.logId}/mark-paid`,
          {
            paid_by: user?.user_id,
            amount_paid: pendingPayment.amount,
            paid_at: paymentDate,
            purpose_of_use: "payment",
          }
        );
        toast.success("Job log marked as paid");
      } else if (pendingPayment.type === "bulk") {
        await Promise.all(
          selectedLogs.map((log_id) => {
            const log = jobLogs.find((l) => l.job_log_id === log_id);
            const amountToPay = Number(log?.amount_to_pay || 0);
            return secureAxios.patch(`/api/job-logs/${log_id}/mark-paid`, {
              paid_by: user?.user_id,
              amount_paid: amountToPay,
              paid_at: paymentDate,
              purpose_of_use: "payment",
            });
          })
        );
        toast.success(
          `${
            selectedLogs.length
          } job logs marked as paid for $${pendingPayment.amount.toFixed(2)}`
        );
        setSelectedLogs([]);
      }

      setShowPaymentModal(false);
      setPendingPayment(null);
      fetchJobLogs();
    } catch (err) {
      console.error("Error processing payment");
      toast.error("Failed to process payment");
    }
  };

  const markSelectedAsPaid = () => {
    if (selectedLogs.length === 0) {
      toast.warning("Please select at least one log to mark as paid");
      return;
    }

    // Calculate total amount for selected logs
    const totalAmount = selectedLogs.reduce((sum, logId) => {
      const log = jobLogs.find((l) => l.job_log_id === logId);
      return sum + Number(log?.amount_to_pay || 0);
    }, 0);

    // Open the payment modal for bulk payment
    setPendingPayment({
      type: "bulk",
      amount: totalAmount,
      count: selectedLogs.length,
    });
    setPaymentDate(new Date().toISOString().split("T")[0]);
    setShowPaymentModal(true);
  };

  const handleSelectLog = (logId) => {
    setSelectedLogs((prev) => {
      if (prev.includes(logId)) {
        return prev.filter((id) => id !== logId);
      } else {
        return [...prev, logId];
      }
    });
  };

  const handleSelectAll = () => {
    const unpaidLogs = filteredLogs.filter((log) => !log.is_paid);
    if (selectedLogs.length === unpaidLogs.length) {
      setSelectedLogs([]);
    } else {
      setSelectedLogs(unpaidLogs.map((log) => log.job_log_id));
    }
  };

  const handleClearFilters = () => {
    setFilters({
      start_date: "",
      end_date: "",
      is_paid: "",
      is_billable: "",
    });
    setSearchTerm("");
    setSelectedLogs([]);
  };

  // Add client-side filtering function
  const getFilteredLogs = () => {
    return jobLogs.filter((log) => {
      // Date filtering (client-side)
      if (filters.start_date) {
        const logDate = new Date(log.date_of_service);
        const startDate = new Date(filters.start_date);
        if (logDate < startDate) return false;
      }

      if (filters.end_date) {
        const logDate = new Date(log.date_of_service);
        const endDate = new Date(filters.end_date);
        if (logDate > endDate) return false;
      }

      // Search term filtering
      if (searchTerm) {
        const searchText = `${log.consumer_name || ""} ${
          log.user_name
        }`.toLowerCase();
        if (!searchText.includes(searchTerm.toLowerCase())) return false;
      }

      return true;
    });
  };

  const filteredLogs = getFilteredLogs();

  const totalAmount = filteredLogs.reduce(
    (sum, log) => sum + Number(log.amount_to_pay || 0),
    0
  );

  const unpaidTotal = filteredLogs
    .filter((log) => !log.is_paid)
    .reduce((sum, log) => sum + Number(log.amount_to_pay || 0), 0);

  const selectedTotal = selectedLogs.reduce((sum, logId) => {
    const log = jobLogs.find((l) => l.job_log_id === logId);
    return sum + Number(log?.amount_to_pay || 0);
  }, 0);

  if (loadingUser) return null;

  if (!is_admin) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <h4>Unauthorized</h4>
          <p>You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mt-4">
        <p>Loading job logs...</p>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <h4>Job Coach Activity Review</h4>

      {/* HIPAA Compliance Notices */}
      <PHIWarningBanner />
      <PurposeOfUseBanner purpose="Payment Processing for Job Coach Services" />

      <div className="row align-items-end mb-3">
        <div className="col-md-2">
          <label className="form-label">Start Date</label>
          <input
            type="date"
            className="form-control"
            value={filters.start_date}
            onChange={(e) =>
              setFilters({ ...filters, start_date: e.target.value })
            }
          />
        </div>

        <div className="col-md-2">
          <label className="form-label">End Date</label>
          <input
            type="date"
            className="form-control"
            value={filters.end_date}
            onChange={(e) =>
              setFilters({ ...filters, end_date: e.target.value })
            }
          />
        </div>

        <div className="col-md-2">
          <label className="form-label">Status</label>
          <select
            className="form-select"
            value={filters.is_paid}
            onChange={(e) =>
              setFilters({ ...filters, is_paid: e.target.value })
            }
          >
            <option value="">All</option>
            <option value="false">Unpaid</option>
            <option value="true">Paid</option>
          </select>
        </div>

        <div className="col-md-2">
          <label className="form-label">Billable Status</label>
          <select
            className="form-select"
            value={filters.is_billable}
            onChange={(e) =>
              setFilters({ ...filters, is_billable: e.target.value })
            }
          >
            <option value="">All</option>
            <option value="true">Billable Only</option>
            <option value="false">Non-billable Only</option>
          </select>
        </div>

        <div className="col-md-4 d-flex justify-content-end align-items-end">
          <button
            className="btn btn-outline-secondary"
            onClick={handleClearFilters}
          >
            Clear Filters
          </button>
        </div>
      </div>

      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search by Consumer or Job Coach"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Total Amount Summary */}
      <div className="alert alert-info mb-3">
        <div className="row">
          <div className="col-md-3">
            <strong>Total Records:</strong> {filteredLogs.length}
          </div>
          <div className="col-md-3">
            <strong>Total Amount:</strong> ${totalAmount.toFixed(2)}
          </div>
          <div className="col-md-3">
            <strong>Unpaid Amount:</strong> ${unpaidTotal.toFixed(2)}
          </div>
          <div className="col-md-3">
            <strong>Selected Amount:</strong> ${selectedTotal.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Bulk action button */}
      {selectedLogs.length > 0 && (
        <div className="mb-3">
          <button className="btn btn-success" onClick={markSelectedAsPaid}>
            Mark {selectedLogs.length} Selected as Paid ($
            {selectedTotal.toFixed(2)})
          </button>
        </div>
      )}

      {(filters.start_date ||
        filters.end_date ||
        filters.is_paid ||
        filters.is_billable) && (
        <div className="mb-3 text-muted">
          <i className="bi bi-funnel-fill"></i> Filters are applied. Use <strong>Clear Filters</strong> to reset
          the list.
        </div>
      )}

      {/* Desktop/tablet view */}
      <div className="table-responsive d-none d-md-block">
        <table className="table table-bordered table-striped mt-3">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={
                    selectedLogs.length > 0 &&
                    selectedLogs.length ===
                      filteredLogs.filter((log) => !log.is_paid).length
                  }
                />
              </th>
              <th>Date</th>
              <th>Consumer</th>
              <th>Job Coach</th>
              <th>Authorization</th>
              <th>Type</th>
              <th>Hours</th>
              <th>Rate</th>
              <th>Amount to Pay</th>
              <th>Paid?</th>
              <th>Date Paid</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.map((log) => (
              <tr key={log.job_log_id}>
                <td>
                  {!log.is_paid && (
                    <input
                      type="checkbox"
                      checked={selectedLogs.includes(log.job_log_id)}
                      onChange={() => handleSelectLog(log.job_log_id)}
                    />
                  )}
                </td>
                <td>{formatDate(log.date_of_service)}</td>
                <td>{log.consumer_name || "Non-billable"}</td>
                <td>{log.user_name}</td>
                <td>
                  {log.authorization_number || "N/A"}
                  {log.service_name && (
                    <div><small className="text-muted">{log.service_name}</small></div>
                  )}
                </td>
                <td>
                  <span
                    className={`badge ${
                      log.is_billable !== false ? "bg-success" : "bg-secondary"
                    }`}
                  >
                    {log.is_billable !== false ? "Billable" : "Non-billable"}
                  </span>
                </td>
                <td>{log.hours_worked}</td>
                <td>
                  $
                  {log.hourly_rate
                    ? Number(log.hourly_rate).toFixed(2)
                    : "0.00"}
                </td>
                <td>${Number(log.amount_to_pay || 0).toFixed(2)}</td>
                <td>
                  {log.is_paid ? (
                    <span className="text-success">Yes</span>
                  ) : (
                    <span className="text-danger">No</span>
                  )}
                </td>
                <td>
                  {log.is_paid && log.paid_at ? (
                    formatDate(log.paid_at)
                  ) : log.previously_paid_at ? (
                    <span
                      className="text-muted"
                      title="Previously paid on this date"
                    >
                      <small>Prev: {formatDate(log.previously_paid_at)}</small>
                    </span>
                  ) : (
                    "-"
                  )}
                </td>
                <td>
                  {!log.is_paid ? (
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => markAsPaid(log.job_log_id)}
                    >
                      Mark as Paid
                    </button>
                  ) : (
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={() => markAsUnpaid(log.job_log_id)}
                    >
                      Mark as Unpaid
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile-friendly stacked cards */}
      <ul className="list-group d-block d-md-none mt-3">
        {filteredLogs.map((log) => (
          <li key={log.job_log_id} className="list-group-item">
            {!log.is_paid && (
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={selectedLogs.includes(log.job_log_id)}
                  onChange={() => handleSelectLog(log.job_log_id)}
                  id={`check-${log.job_log_id}`}
                />
                <label
                  className="form-check-label"
                  htmlFor={`check-${log.job_log_id}`}
                >
                  Select for bulk payment
                </label>
              </div>
            )}
            <div>
              <strong>Date:</strong> {formatDate(log.date_of_service)}
            </div>
            <div>
              <strong>Consumer:</strong> {log.consumer_name || "Non-billable"}
            </div>
            <div>
              <strong>Job Coach:</strong> {log.user_name}
            </div>
            <div>
              <strong>Authorization:</strong>{" "}
              {log.authorization_number || "N/A"}
              {log.service_name && (
                <div><small className="text-muted">{log.service_name}</small></div>
              )}
            </div>
            <div>
              {" "}
              <strong>Type:</strong>{" "}
              <span
                className={`badge ${
                  log.is_billable !== false ? "bg-success" : "bg-secondary"
                }`}
              >
                {log.is_billable !== false ? "Billable" : "Non-billable"}
              </span>
            </div>
            <div>
              <strong>Hours:</strong> {log.hours_worked}
            </div>
            <div>
              <strong>Rate:</strong> $
              {log.hourly_rate ? Number(log.hourly_rate).toFixed(2) : "0.00"}
            </div>
            <div>
              <strong>Amount to Pay:</strong> $
              {Number(log.amount_to_pay || 0).toFixed(2)}
            </div>
            <div>
              <strong>Status:</strong>{" "}
              {log.is_paid ? (
                <span className="text-success">Paid</span>
              ) : (
                <span className="text-danger">Unpaid</span>
              )}
            </div>
            {log.is_paid && log.paid_at && (
              <div>
                <strong>Date Paid:</strong> {formatDate(log.paid_at)}
              </div>
            )}
            {!log.is_paid && log.previously_paid_at && (
              <div className="text-muted">
                <strong>Previously Paid:</strong>{" "}
                {formatDate(log.previously_paid_at)}
              </div>
            )}

            {!log.is_paid ? (
              <div className="mt-2">
                <button
                  className="btn btn-sm btn-success"
                  onClick={() => markAsPaid(log.job_log_id)}
                >
                  Mark as Paid
                </button>
              </div>
            ) : (
              <div className="mt-2">
                <button
                  className="btn btn-sm btn-warning"
                  onClick={() => markAsUnpaid(log.job_log_id)}
                >
                  Mark as Unpaid
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>

      {/* Data Handling Notice */}
      <DataHandlingNotice />

      {/* Payment Date Modal */}
      {showPaymentModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Payment</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPendingPayment(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  {pendingPayment?.type === "single" ? (
                    <>
                      Mark this job log as paid for{" "}
                      <strong>${pendingPayment?.amount?.toFixed(2)}</strong>?
                    </>
                  ) : (
                    <>
                      Mark <strong>{pendingPayment?.count}</strong> job logs as
                      paid for{" "}
                      <strong>${pendingPayment?.amount?.toFixed(2)}</strong>?
                    </>
                  )}
                </p>

                <div className="mb-3">
                  <label htmlFor="paymentDate" className="form-label">
                    Payment Date
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    id="paymentDate"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                  />
                  <small className="text-muted">
                    Enter the date the payment was or will be made
                  </small>
                </div>

                <div className="alert alert-info small mb-0">
                  <strong>By confirming, you acknowledge that:</strong>
                  <ul className="mb-0 mt-2">
                    <li>This action will be logged for audit purposes</li>
                    <li>You have verified the accuracy of this payment</li>
                    <li>You are authorized to process this payment</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPendingPayment(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={confirmPayment}
                >
                  Confirm Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobLogReviewPage;